import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { LocalStorage } from "../commons/LocalStorage";
import { noPropagateDialog$ } from "src/store/global.store";
import { appStore, isLoading$ } from "../store/AppStore";
import { doLogOut, getContaUrlCompleto, isLoggedIn$ } from "../store/LoginStore";
import { notifierStore } from "../components/commons/dialogs/Notifier/store";

export enum STATUS_CODE {
  NO_CONTENT = 204,
  OK = 200,
  CREATED = 201,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  INTERNAL_SERVER_ERROR = 500,
}

export interface IDataRequest {
  url: string;
  data?: object;
  headers?: object;
  withCredentials?: boolean;
  guarded?: boolean;
  onUploadProgress?: (progressEvent: any) => void
}

export interface IDataResponse<T = any> {
  data: T;
  statusCode: number;
  message: string;
  headers?: Record<string, string>
}

export interface IHttpClient<T = any> {
  api: T;

  get(data: IDataRequest): Promise<IDataResponse<T>>;
  post(data: IDataRequest): Promise<IDataResponse<T>>;
  put(data: IDataRequest): Promise<IDataResponse<T>>;
  delete(data: IDataRequest): Promise<IDataResponse<T>>;

  getFile(data: IDataRequest): Promise<IDataResponse>;
}

class Api implements IHttpClient<AxiosInstance> {
  api: AxiosInstance;

  constructor(baseUrl: string, readonly noLoading: boolean = false) {
    this.api = axios.create({
      baseURL: baseUrl,
      withCredentials: false,
    });

    this.api.interceptors.request.use((request) => {
      if (!this.noLoading) {
        isLoading$.next(true);
      }

      if (isLoggedIn$.getValue()) {
        request.headers.set("Authorization", `Bearer ${LocalStorage.get("solar.token")}`);
      }
      return request;
    });
    this.api.interceptors.response.use(
      (response) => {
        if (response.headers.newtoken) {
          LocalStorage.set("solar.token", response.headers.newtoken)
        }
        isLoading$.next(false);
        return response;
      },
      (error: AxiosError) => {
        isLoading$.next(false);
        if (error?.response?.status === STATUS_CODE.UNAUTHORIZED) {
          notifierStore.setNotifier({
            title: "Aviso!",
            onClose: async () => {
              const contaUrl = await getContaUrlCompleto(window.location.origin)

              if (contaUrl) {
                doLogOut();
                LocalStorage.remove("solar.token")
                window.location.href = contaUrl
              }
            },
            content: "Seu acesso à loja de kits expirou. Para continuar, efetue o login novamente.",
          })
          notifierStore.open();
          noPropagateDialog$.next(true)
        } else if ((error?.response?.data as any)?.message) {
          appStore.setError([...(error?.response?.data as any)?.message]);
          if (error.response?.headers["conta-post-logout-redirect-url"]) {
            return error
          }
        } /* else {
          appStore.setError([error]);
        } */
      }
    );
  }

  async getFile(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.get(data.url, {
        headers: data.headers,
        responseType: "arraybuffer",
      });

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nenhum arquivo retornado",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async get(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.get(data.url, {
        headers: data.headers,
      });
      if (_resp === undefined) {
        return {
          data: [],
          message: "Api está indisponível",
          statusCode: 500,
        };
      }

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp.data.message ? _resp.data.message : "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async post(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse & { response?: any } = await this.api.post(data.url, data.data, {
        headers: data.headers || {},
        onUploadProgress: data?.onUploadProgress
      });

      if (!_resp || _resp?.response?.headers?.["conta-post-logout-redirect-url"]) {
        return {
          data: [],
          message: appStore.getError(),
          statusCode: 400,
          headers: _resp?.response?.headers
        };
      }

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp?.data?.message || "OK",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async put(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.put(data.url, data.data, {
        headers: data.headers || {},
      });

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nenhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: _resp?.data?.message || "OK",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }

  async delete(data: IDataRequest): Promise<IDataResponse> {
    try {
      let _resp: AxiosResponse = await this.api.delete(data.url, data.data);

      if (_resp.status === STATUS_CODE.NO_CONTENT) {
        return {
          data: [],
          message: "Nunhum conteúdo a ser exibido",
          statusCode: _resp.status,
        };
      }

      return {
        data: _resp.data.data,
        message: "Ok",
        statusCode: _resp.status,
      };
    } catch (error) {
      return {
        data: null,
        message: "Error",
        statusCode: STATUS_CODE.INTERNAL_SERVER_ERROR,
      };
    }
  }
}

const api = new Api(`${process.env.REACT_APP_API_BASE_URL}`);
const apiNoLoading = new Api(`${process.env.REACT_APP_API_BASE_URL}`, true);


export default api;
export { apiNoLoading };
